import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Stack, IconButton, Chip,
  CircularProgress, Tooltip,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon  from '@mui/icons-material/ChevronLeft';
import { HEBREW_MONTH_NAMES, SLOT_TYPE_LABELS, SLOT_TYPE_COLORS, toHebrewYear } from '../../../business/ParnasWidget/parnasConstants';

const navy = '#1e3a5f';

// ─── Hebrew number display ────────────────────────────────────────────────────
const HEBREW_LETTERS = [
  '', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט', 'י',
  'יא', 'יב', 'יג', 'יד', 'טו', 'טז', 'יז', 'יח', 'יט', 'כ',
  'כא', 'כב', 'כג', 'כד', 'כה', 'כו', 'כז', 'כח', 'כט', 'ל',
];
const toHebrewNum = (n) => HEBREW_LETTERS[n] || String(n);

// (month navigation is handled via HebCal converter API — see handleNav)


// ─── API cache ────────────────────────────────────────────────────────────────
const calendarCache = {};

const fetchMonthData = async (hYear, hMonth) => {
  const key = `${hYear}-${hMonth}`;
  if (calendarCache[key]) return calendarCache[key];

  const url =
    `https://www.hebcal.com/hebcal?v=1&cfg=json` +
    `&year=${hYear}&month=${hMonth}&yt=H` +
    `&d=on&maj=on&min=on&nx=on&ss=on&mf=on&s=on`;

  const res  = await fetch(url);
  const json = await res.json();
  const items = [...(json.items || [])];

  // d=on with yt=H doesn't always return hebdate items in all API versions.
  // Fall back to generating them via the converter API.
  const hasHebdate = items.some(i => i.category === 'hebdate');
  if (!hasHebdate) {
    try {
      const hMonthName = HMONTH_ENG[hMonth] || 'Nisan';
      const convUrl = `https://www.hebcal.com/converter?cfg=json&hy=${hYear}&hm=${encodeURIComponent(hMonthName)}&hd=1&h2g=1`;
      const conv = await (await fetch(convUrl)).json();
      if (conv.gy && conv.gm && conv.gd) {
        const monthLen = conv.monthLen || 30;
        const start = new Date(conv.gy, conv.gm - 1, conv.gd);
        for (let hd = 1; hd <= monthLen; hd++) {
          const gd = new Date(start.getFullYear(), start.getMonth(), start.getDate() + hd - 1);
          const isoDate = `${gd.getFullYear()}-${String(gd.getMonth() + 1).padStart(2, '0')}-${String(gd.getDate()).padStart(2, '0')}`;
          items.push({ category: 'hebdate', date: isoDate, hd, hm: hMonthName, hy: hYear, title: `${hd} ${hMonthName} ${hYear}` });
        }
      }
    } catch (_) { /* silently ignore — grid will be empty */ }
  }

  calendarCache[key] = items;
  return items;
};

// ─── Build day map from items ─────────────────────────────────────────────────
const buildDayMap = (items) => {
  // dayMap: { 'YYYY-MM-DD': { hd, events: [], parashat: null } }
  const map = {};
  const ensure = (date) => {
    if (!map[date]) map[date] = { gregDate: date, hd: null, events: [], parashat: null, isShabbat: false };
  };
  for (const item of items) {
    const date = item.date?.substring(0, 10);
    if (!date) continue;
    ensure(date);

    if (item.category === 'hebdate') {
      const hd = typeof item.hd === 'number' ? item.hd : parseInt(item.title, 10);
      map[date].hd = isNaN(hd) ? null : hd;
    } else if (item.category === 'parashat') {
      map[date].parashat = item.hebrew || item.title;
      map[date].isShabbat = true;
    } else if (['holiday', 'roshchodesh', 'fast'].includes(item.category)) {
      map[date].events.push({ label: item.hebrew || item.title, category: item.category });
    }
  }
  return map;
};

// ─── English month names for converter API (exact HebCal spellings) ──────────
const HMONTH_ENG = ['', 'Nisan', 'Iyyar', 'Sivan', 'Tamuz', 'Av', 'Elul',
                    'Tishrei', 'Cheshvan', 'Kislev', 'Tevet', "Sh'vat", 'Adar', 'Adar II'];

// ─── Get today's Hebrew month/year ───────────────────────────────────────────
const MONTH_NAME_TO_NUM = {
  'Nisan': 1,
  'Iyyar': 2, 'Iyar': 2,                        // HebCal returns 'Iyyar'
  'Sivan': 3, 'Tamuz': 4, 'Tammuz': 4,
  'Av': 5, 'Elul': 6, 'Tishrei': 7, 'Tishri': 7,
  'Cheshvan': 8, 'Heshvan': 8,
  'Kislev': 9, 'Tevet': 10,
  "Sh'vat": 11, 'Shvat': 11,                    // HebCal returns "Sh'vat"
  'Adar1': 12, 'Adar': 12, 'Adar I': 12,
  'Adar2': 13, 'Adar II': 13,
};

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * HebrewCalendarPicker
 * לוח שנה עברי לבחירת יום / שבוע (פרשה) / חודש.
 *
 * Props:
 *   existingSlots    — כל ה-slots הקיימים (לסימון תפוסים)
 *   sponsors         — רשימת הפרנסים (להצגת שמות על הלוח)
 *   slotType         — 'daily' | 'weekly' | 'monthly' | null  (מצב בחירה פעיל)
 *   onSlotTypeChange — (type) => void  (שינוי מצב בחירה)
 *   onSelect         — ({ type, date?, parshaName?, parshaShabbatDate?, hebrewMonth?, hebrewYear?, hebrewMonthName?, hDate? }) => void
 *   selected         — הערך שנבחר כרגע (לסימון)
 */
const HebrewCalendarPicker = ({
  existingSlots = [],
  sponsors = [],
  slotType = null,
  onSlotTypeChange,
  onSelect,
  selected = null,
  readOnly = false,
}) => {
  const [currentHMonth, setCurrentHMonth] = useState(null);
  const [currentHYear,  setCurrentHYear]  = useState(null);
  const [dayMap,   setDayMap]   = useState({});
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const initialized = useRef(false);

  // ─── Init: קבל חודש עברי נוכחי ───────────────────────────────────────────
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const today = todayISO();
    fetch(`https://www.hebcal.com/converter?cfg=json&date=${today}&g2h=1`)
      .then(r => r.json())
      .then(data => {
        const hm = MONTH_NAME_TO_NUM[data.hm] ?? 7;
        setCurrentHMonth(hm);
        setCurrentHYear(data.hy);
      })
      .catch(() => {
        setCurrentHMonth(7);
        setCurrentHYear(5785);
      });
  }, []);

  // ─── טען נתוני חודש ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentHMonth || !currentHYear) return;
    // If handleNav already fetched and cached this month's data, just update dayMap
    const cacheKey = `${currentHYear}-${currentHMonth}`;
    if (calendarCache[cacheKey]) {
      setDayMap(buildDayMap(calendarCache[cacheKey]));
      return;
    }
    setLoading(true);
    setError(null);
    fetchMonthData(currentHYear, currentHMonth)
      .then(items => {
        setDayMap(buildDayMap(items));
        setLoading(false);
      })
      .catch(() => {
        setError('שגיאה בטעינת הלוח');
        setLoading(false);
      });
  }, [currentHMonth, currentHYear]);

  // ─── Compute sorted greg dates in month ──────────────────────────────────
  const sortedDates = Object.keys(dayMap)
    .filter(d => dayMap[d].hd != null)
    .sort();

  // Build weeks (rows of 7 days, Sun=0..Sat=6)
  const buildWeeks = () => {
    if (sortedDates.length === 0) return [];
    const weeks = [];
    let week = Array(7).fill(null);
    for (const d of sortedDates) {
      const dow = new Date(d).getDay(); // 0=Sun..6=Sat
      week[dow] = d;
      if (dow === 6) {
        weeks.push(week);
        week = Array(7).fill(null);
      }
    }
    // last partial week
    if (week.some(Boolean)) weeks.push(week);
    return weeks;
  };
  const weeks = buildWeeks();

  // ─── Helpers: occupation ─────────────────────────────────────────────────
  const getSlotsForDate = (gregDate) => {
    const result = [];
    const day = dayMap[gregDate];
    if (!day) return result;

    for (const sponsor of sponsors) {
      for (const slot of (sponsor.slots || [])) {
        let match = false;
        if (slot.type === 'daily' && slot.date === gregDate) {
          match = true;
        } else if (slot.type === 'weekly') {
          // a weekly slot covers the entire week of its parshaShabbatDate
          const shabbat = slot.parshaShabbatDate;
          if (shabbat) {
            const shabbatDay = new Date(shabbat);
            const startOfWeek = new Date(shabbatDay);
            startOfWeek.setDate(shabbatDay.getDate() - 6); // Sunday
            const gd = new Date(gregDate);
            match = gd >= startOfWeek && gd <= shabbatDay;
          }
        } else if (slot.type === 'monthly') {
          // monthly covers entire Hebrew month
          if (day.hd !== null) {
            // check hMonth from the items — use currentHMonth as proxy
            // (all days in this calendar page belong to currentHMonth)
            match = slot.hebrewMonth === currentHMonth && slot.hebrewYear === currentHYear;
          }
        }
        if (match) {
          result.push({ sponsorName: sponsor.name, slotType: slot.type });
        }
      }
    }
    return result;
  };

  const getParashaForWeek = (weekDates) => {
    const shabbat = weekDates[6];
    if (!shabbat) return null;
    return dayMap[shabbat]?.parashat || null;
  };

  const getParashaShabbatDate = (weekDates) => weekDates[6] || null;

  const isMonthOccupied = existingSlots.some(
    s => s.type === 'monthly' && s.hebrewMonth === currentHMonth && s.hebrewYear === currentHYear
  );

  // ─── Selection checks ────────────────────────────────────────────────────
  const isDaySelected = (gregDate) =>
    slotType === 'daily' && selected?.date === gregDate;

  const isWeekSelected = (weekDates) =>
    slotType === 'weekly' && selected?.parshaShabbatDate === (weekDates[6] || null);

  const isMonthSelected = () =>
    slotType === 'monthly' && selected?.hebrewMonth === currentHMonth && selected?.hebrewYear === currentHYear;

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleDayClick = (gregDate) => {
    if (slotType !== 'daily') return;
    const day = dayMap[gregDate];
    const existingDaily = existingSlots.find(s => s.type === 'daily' && s.date === gregDate);
    if (existingDaily) return; // already taken
    onSelect({
      type: 'daily',
      date: gregDate,
      hDate: day ? `${toHebrewNum(day.hd)} ${HEBREW_MONTH_NAMES[currentHMonth]} ${toHebrewYear(currentHYear)}` : '',
    });
  };

  const handleWeekClick = (weekDates) => {
    if (slotType !== 'weekly') return;
    const shabbatDate = weekDates[6];
    if (!shabbatDate) return;
    const parashaName = dayMap[shabbatDate]?.parashat || '';
    const existingWeekly = existingSlots.find(s => s.type === 'weekly' && s.parshaShabbatDate === shabbatDate);
    if (existingWeekly) return;
    onSelect({ type: 'weekly', parshaName: parashaName, parshaShabbatDate: shabbatDate });
  };

  const handleMonthClick = () => {
    if (slotType !== 'monthly') return;
    if (isMonthOccupied) return;
    onSelect({
      type: 'monthly',
      hebrewMonth: currentHMonth,
      hebrewYear:  currentHYear,
      hebrewMonthName: HEBREW_MONTH_NAMES[currentHMonth] || '',
    });
  };

  const handleNav = async (dir) => {
    if (sortedDates.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      // Always anchor on sortedDates[0]:
      //   next → +31 days (max Hebrew month is 30) = definitely in next month
      //   prev → -1 day = last day of previous month
      const d = new Date(sortedDates[0]);
      d.setDate(d.getDate() + (dir === 'next' ? 31 : -1));
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      // Ask HebCal what Hebrew month that gregorian date belongs to
      const conv = await (await fetch(`https://www.hebcal.com/converter?cfg=json&date=${iso}&g2h=1`)).json();
      const newHMonth = MONTH_NAME_TO_NUM[conv.hm];
      if (newHMonth != null && conv.hy) {
        // Fetch month data directly here so loading is fully owned by handleNav
        const items = await fetchMonthData(conv.hy, newHMonth);
        setDayMap(buildDayMap(items));
        setCurrentHMonth(newHMonth);
        setCurrentHYear(conv.hy);
        // useEffect will fire but find data in cache → no extra loading
      }
    } catch (_) {
      setError('שגיאה בניווט');
    } finally {
      setLoading(false); // always clears — no stuck loading possible
    }
  };

  // ─── Event color ─────────────────────────────────────────────────────────
  const eventColor = (cat) => ({
    holiday:     '#b45309',
    roshchodesh: '#1e3a5f',
    fast:        '#6b7280',
  }[cat] || '#6b7280');

  // ─── Render ───────────────────────────────────────────────────────────────
  const DAY_LABELS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

  if (!currentHMonth) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box dir="rtl" sx={{ border: '1px solid #e5e7eb', borderRadius: 2, overflow: 'hidden', bgcolor: 'white' }}>

      {/* ── Header: month name + navigation ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2, py: 1.25, borderBottom: '1px solid #e5e7eb', bgcolor: '#fafafa',
      }}>
        <IconButton size="small" onClick={() => handleNav('prev')} disabled={loading}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>

        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ fontWeight: 700, color: navy, fontSize: '0.95rem' }}>
            {HEBREW_MONTH_NAMES[currentHMonth]} {toHebrewYear(currentHYear)}
          </Typography>
          {isMonthSelected() && (
            <Chip label="✓ נבחר חודש שלם" size="small" sx={{ bgcolor: '#dcfce7', color: '#065f46', fontSize: '0.7rem', height: 18, mt: 0.25 }} />
          )}
        </Box>

        <IconButton size="small" onClick={() => handleNav('next')} disabled={loading}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Legend — shown only in readOnly ── */}
      {readOnly && (
        <Box sx={{ display: 'flex', gap: 2.5, px: 2, py: 1, borderBottom: '1px solid #f0f0f0', flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, ml: 0.5 }}>מקרא:</Typography>
          {([
            { label: 'נציב יום',   color: SLOT_TYPE_COLORS.daily   },
            { label: 'נציב שבוע',  color: SLOT_TYPE_COLORS.weekly  },
            { label: 'נציב חודש',  color: SLOT_TYPE_COLORS.monthly },
            { label: 'פנוי',       color: '#d1d5db'                 },
          ]).map(({ label, color }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#374151', fontWeight: 500 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* ── Type selector chips — hidden in readOnly ── */}
      {!readOnly && <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1, borderBottom: '1px solid #f0f0f0' }}>
        {(['daily', 'weekly', 'monthly']).map((type) => {
          const isActive = slotType === type;
          return (
            <Chip
              key={type}
              label={SLOT_TYPE_LABELS[type]}
              size="small"
              onClick={() => onSlotTypeChange(isActive ? null : type)}
              sx={{
                cursor: 'pointer',
                bgcolor: isActive ? navy : 'transparent',
                color:   isActive ? 'white' : '#6b7280',
                border: `1px solid ${isActive ? navy : '#d1d5db'}`,
                fontWeight: isActive ? 600 : 400,
                fontSize: '0.78rem',
                '&:hover': { bgcolor: isActive ? '#152a47' : '#f3f4f6' },
              }}
            />
          );
        })}
        <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', alignSelf: 'center', mr: 'auto' }}>
          {slotType ? `לחץ על ${slotType === 'daily' ? 'יום' : slotType === 'weekly' ? 'שורת שבוע' : 'כפתור חודש'}` : 'בחר סוג נציבות'}
        </Typography>
      </Box>}

      {/* ── Calendar grid ── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Typography sx={{ textAlign: 'center', color: '#ef4444', fontSize: '0.8rem', py: 2 }}>{error}</Typography>
      ) : (
        <Box sx={{ p: 1 }}>
          {/* Day-of-week header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'auto repeat(7, 1fr)', gap: '1px', mb: '1px' }}>
            <Box /> {/* spacer for week label */}
            {DAY_LABELS.map((d, i) => (
              <Typography key={i} sx={{
                textAlign: 'center', fontSize: '0.72rem', fontWeight: 600,
                color: i === 6 ? '#b45309' : '#9ca3af', pb: 0.5,
              }}>
                {d}
              </Typography>
            ))}
          </Box>

          {/* Weeks */}
          {weeks.map((week, wi) => {
            const parashaName = getParashaForWeek(week);
            const shabbatDate = getParashaShabbatDate(week);
            const isWeekSel   = isWeekSelected(week);
            const weekOccupied = existingSlots.find(
              s => s.type === 'weekly' && s.parshaShabbatDate === shabbatDate
            );
            // find who occupies this week
            const weekOccupier = weekOccupied
              ? sponsors.find(sp => sp.slots?.some(sl => sl.type === 'weekly' && sl.parshaShabbatDate === shabbatDate))
              : null;

            return (
              <Box
                key={wi}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'auto repeat(7, 1fr)',
                  gap: '1px',
                  mb: '2px',
                  borderRadius: 1,
                  ...(isWeekSel ? { outline: `2px solid ${SLOT_TYPE_COLORS.weekly}`, outlineOffset: '-1px' } : {}),
                }}
              >
                {/* Week label */}
                <Tooltip
                  title={
                    weekOccupier
                      ? `${weekOccupier.name} — נציב שבוע`
                      : parashaName
                      ? `פרשת ${parashaName}${slotType === 'weekly' && !weekOccupied ? ' — לחץ לבחירה' : ''}`
                      : ''
                  }
                  placement="right"
                >
                  <Box
                    onClick={() => !readOnly && handleWeekClick(week)}
                    sx={{
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      gap: '2px',
                      width: (readOnly || slotType === 'weekly') ? 54 : 16,
                      minHeight: 52,
                      cursor: !readOnly && slotType === 'weekly' && !weekOccupied ? 'pointer' : 'default',
                      borderRadius: 1,
                      transition: 'background-color 0.15s',
                      bgcolor: isWeekSel
                        ? SLOT_TYPE_COLORS.weekly
                        : weekOccupied
                        ? '#ede9fe'
                        : (readOnly || slotType === 'weekly')
                        ? '#f0ebff'
                        : 'transparent',
                      border: (readOnly || slotType === 'weekly')
                        ? `1.5px solid ${isWeekSel ? SLOT_TYPE_COLORS.weekly : '#c4b5fd'}`
                        : 'none',
                      boxShadow: !readOnly && slotType === 'weekly' && !weekOccupied && !isWeekSel
                        ? '0 1px 4px rgba(124,58,237,0.18)'
                        : 'none',
                      '&:hover': !readOnly && slotType === 'weekly' && !weekOccupied
                        ? { bgcolor: '#ddd6fe', borderColor: SLOT_TYPE_COLORS.weekly }
                        : {},
                    }}
                  >
                    {(parashaName || readOnly || slotType === 'weekly') && (
                      <Typography
                        sx={{
                          fontSize: (readOnly || slotType === 'weekly') ? '0.65rem' : '0.55rem',
                          fontWeight: (readOnly || slotType === 'weekly') ? 700 : 400,
                          color: isWeekSel ? 'white'
                            : weekOccupied ? '#7c3aed'
                            : (readOnly || slotType === 'weekly') ? '#7c3aed'
                            : '#9ca3af',
                          writingMode: 'vertical-rl',
                          transform: 'rotate(180deg)',
                          lineHeight: 1,
                          maxHeight: 50,
                          overflow: 'hidden',
                        }}
                      >
                        {parashaName
                          ? parashaName.replace('פרשת ', '').slice(0, (readOnly || slotType === 'weekly') ? 8 : 6)
                          : 'שבוע'}
                      </Typography>
                    )}
                    {slotType === 'weekly' && !weekOccupied && !isWeekSel && (
                      <Typography sx={{
                        fontSize: '0.5rem', color: '#7c3aed', lineHeight: 1,
                        writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                      }}>
                        ▶
                      </Typography>
                    )}
                  </Box>
                </Tooltip>

                {/* Day cells */}
                {week.map((gregDate, di) => {
                  if (!gregDate) {
                    return <Box key={di} sx={{ minHeight: 52 }} />;
                  }
                  const day      = dayMap[gregDate];
                  const slots    = getSlotsForDate(gregDate);
                  const isSel    = isDaySelected(gregDate);
                  const dailyOcc = existingSlots.find(s => s.type === 'daily' && s.date === gregDate);
                  const isClickable = !readOnly && slotType === 'daily' && !dailyOcc;
                  const isShabbat   = di === 6;
                  const isToday     = gregDate === todayISO();

                  // Compute greg display (day number)
                  const gregDay = new Date(gregDate).getDate();

                  return (
                    <Tooltip
                      key={di}
                      title={
                        dailyOcc && slotType === 'daily'
                          ? `תפוס ע"י ${sponsors.find(sp => sp.slots?.some(sl => sl.type === 'daily' && sl.date === gregDate))?.name || ''} — נציב יום`
                          : ''
                      }
                      placement="top"
                    >
                      <Box
                        onClick={() => isClickable && handleDayClick(gregDate)}
                        sx={{
                          minHeight: 52,
                          minWidth: 0,
                          overflow: 'hidden',
                          p: '3px',
                          borderRadius: 1,
                          cursor: isClickable ? 'pointer' : 'default',
                          border: isSel
                            ? `2px solid ${SLOT_TYPE_COLORS.daily}`
                            : isToday
                            ? '1px solid #93c5fd'
                            : '1px solid #f3f4f6',
                          bgcolor: isSel
                            ? '#eff6ff'
                            : dailyOcc
                            ? '#f5f3ff'
                            : isShabbat
                            ? '#fefce8'
                            : 'white',
                          '&:hover': isClickable ? { bgcolor: '#eff6ff', borderColor: SLOT_TYPE_COLORS.daily } : {},
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '1px',
                        }}
                      >
                        {/* Hebrew + Gregorian day number */}
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Typography sx={{
                            fontSize: '0.72rem',
                            fontWeight: isToday ? 700 : 500,
                            color: isShabbat ? '#b45309' : isToday ? navy : '#374151',
                            lineHeight: 1,
                          }}>
                            {day?.hd ? toHebrewNum(day.hd) : ''}
                          </Typography>
                          <Typography sx={{ fontSize: '0.6rem', color: '#9ca3af', lineHeight: 1 }}>
                            {gregDay}
                          </Typography>
                        </Stack>

                        {/* Events */}
                        {day?.events?.slice(0, 1).map((ev, ei) => (
                          <Typography key={ei} sx={{
                            fontSize: '0.55rem',
                            color: eventColor(ev.category),
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}>
                            {ev.label}
                          </Typography>
                        ))}

                        {/* Parasha (for shabbat) */}
                        {day?.parashat && (
                          <Typography sx={{
                            fontSize: '0.55rem',
                            color: '#b45309',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                          }}>
                            {day.parashat.replace('פרשת ', '')}
                          </Typography>
                        )}

                        {/* Existing sponsors on this day */}
                        {slots.map((s, si) => {
                          const typePrefix = s.slotType === 'daily' ? 'יומי' : s.slotType === 'weekly' ? 'שבועי' : 'חודשי';
                          return (
                            <Tooltip key={si} title={`${s.sponsorName} — נציב ${typePrefix === 'יומי' ? 'יום' : typePrefix === 'שבועי' ? 'שבוע' : 'חודש'}`} placement="top">
                              <Typography sx={{
                                fontSize: '0.55rem',
                                color: SLOT_TYPE_COLORS[s.slotType],
                                fontWeight: 600,
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                bgcolor: s.slotType === 'daily' ? '#eff6ff' : s.slotType === 'weekly' ? '#f5f3ff' : '#f0fdf4',
                                borderRadius: '2px',
                                px: '2px',
                              }}>
                                {typePrefix} · {s.sponsorName}
                              </Typography>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── "Select entire month" button ── */}
      {!readOnly && slotType === 'monthly' && (
        <Box sx={{ px: 2, pb: 1.5 }}>
          <Box
            onClick={!isMonthOccupied ? handleMonthClick : undefined}
            sx={{
              border: isMonthSelected()
                ? `2px solid ${SLOT_TYPE_COLORS.monthly}`
                : isMonthOccupied
                ? '1px solid #d1d5db'
                : '1.5px dashed #6ee7b7',
              borderRadius: 1.5,
              py: 0.75,
              px: 2,
              textAlign: 'center',
              cursor: isMonthOccupied ? 'not-allowed' : 'pointer',
              bgcolor: isMonthSelected() ? '#f0fdf4' : isMonthOccupied ? '#f9fafb' : 'white',
              '&:hover': !isMonthOccupied ? { bgcolor: '#f0fdf4' } : {},
            }}
          >
            <Typography sx={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: isMonthSelected() ? SLOT_TYPE_COLORS.monthly : isMonthOccupied ? '#9ca3af' : SLOT_TYPE_COLORS.monthly,
            }}>
              {isMonthSelected()
                ? `✓ נבחר — ${HEBREW_MONTH_NAMES[currentHMonth]} ${toHebrewYear(currentHYear)}`
                : isMonthOccupied
                ? `${HEBREW_MONTH_NAMES[currentHMonth]} — תפוס`
                : `בחר חודש שלם — ${HEBREW_MONTH_NAMES[currentHMonth]} ${toHebrewYear(currentHYear)}`
              }
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default HebrewCalendarPicker;
